/**
 * Bootstrap do painel: detecta marca, inicializa i18n (idioma ativo), monta React Query + Router.
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';

// Tokens do design system primeiro: os CSS seguintes podem usar var(--tinta), var(--linha)...
import './styles/tokens.css';
// Movimento do design system (.ds-giro, .ds-esqueleto, .is-aberto...): antes dos CSS das telas,
// que mantêm forma e cor de cada peça.
import './styles/movimento.css';
import './styles/global.css';
import './styles/app.css';
import './styles/loading.css';
import i18next from 'i18next';
import { getBrand } from './config/brand';
import { setupI18n } from './i18n';
import { cachedLanguage } from './lib/panelLanguage';
import { queryClient } from './lib/queryClient';
import { router } from './routes';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { installChunkReload } from './lib/chunkReload';
import { ShellSkeleton } from './components/common/Skeleton';
import { MovimentoRaiz } from './components/common/MovimentoRaiz';
import { deviceSupported } from './lib/deviceCheck';
import { initRequestContext } from './lib/requestContext';
import { startActivity } from './lib/activity';
import { installActivityObservers } from './lib/activity/observers';

async function bootstrap(): Promise<void> {
  // Aba aberta durante uma publicação: arquivo de tela antigo some → recarrega uma vez.
  installChunkReload();
  const brand = getBrand();

  try {
    document.title = brand.appTitle;
  } catch {
    /* noop */
  }

  // Idioma inicial: o escolhido em Conta > Preferências neste navegador ou, sem escolha, o da
  // marca. A escolha guardada no servidor chega depois da abertura (usePanelLanguage).
  await setupI18n(cachedLanguage() ?? brand.defaultLang);

  const rootEl = document.getElementById('root');
  if (!rootEl) throw new Error('#root não encontrado');

  // Gate de dispositivo/navegador (system.js): mobile ou navegador sem suporte não montam. O link
  // de autorização do Watchdog (/authorize) passa: o email costuma ser aberto no celular.
  if (
    window.location.pathname !== '/authorize' &&
    !deviceSupported(rootEl, (key, fallback) => i18next.t(key, { defaultValue: fallback }))
  ) {
    return;
  }

  // Resolve o contexto de request (fingerprint + geo/version da extensão) ANTES do render,
  // para o GET /app/me já sair com todos os headers — como o legado resolve `sync`+fingerprint
  // antes do getMe [application.js:1556-1596]. Tem teto de tempo interno (não trava o boot).
  await initRequestContext();

  // Registro de uso (decisão 172): pool no navegador e envio em lotes; nada sai por evento.
  startActivity();
  installActivityObservers(router);

  createRoot(rootEl).render(
    <StrictMode>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <MovimentoRaiz>
            <RouterProvider router={router} fallbackElement={<ShellSkeleton />} />
          </MovimentoRaiz>
        </QueryClientProvider>
      </ErrorBoundary>
    </StrictMode>,
  );
}

void bootstrap();
